import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import clsx from "clsx";
import Lottie from "react-lottie";
import { uploadToS3, screenerStart, screenerSubmit, screenerInitVoice } from "../resources/api";
import { waitForVoice, initiateVolumeMeter } from "../utils/audios";
import { getSpeechConfig } from "../utils/speech";
import { CorrectIcon, RecordIcon, StopRecordIcon, WrongIcon } from "../resources/svgs";
import * as processingAnimation from "../lottie/processing.json";
import FlipNumbers from "react-flip-numbers";
import wait from "../utils/wait";
import styles from "../styles/Screener.module.css";

const CHIME = "https://d3vx0tne0vxeo1.cloudfront.net/audios/chimes";
const KG_LETTERS = new Set(["I", "q", "a"]);

const ClockIcon = () => (
    <svg width={22} height={22} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M77 5L95 19.4M23 5L5 19.4M80.6 87.8L87.8 95M19.4 87.8L12.2 95M51.8 41V59L41 66.2M87.8 57.2C87.8 78.0764 70.8764 95 50 95C29.1236 95 12.2 78.0764 12.2 57.2C12.2 36.3236 29.1236 19.4 50 19.4C70.8764 19.4 87.8 36.3236 87.8 57.2Z"
            stroke="white" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const PauseIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="5" width="4" height="14" rx="1.5" />
        <rect x="14" y="5" width="4" height="14" rx="1.5" />
    </svg>
);
const ArrowIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 12h15M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const Countdown = ({ seconds }) => (
    <div className={clsx(styles.actionContainer, styles.active, seconds <= 3 && styles.low)}>
        <div className={styles.contentContainer}>
            <div className={styles.countDownContainer}>
                <ClockIcon />
                <h1 style={{ margin: 0 }}>
                    <FlipNumbers height={30} width={20} numberStyles={styles.numberStyles} color="white" play numbers={String(seconds)} />
                </h1>
            </div>
        </div>
    </div>
);

function play(url, channel = "screener") {
    if (!url) return Promise.resolve();
    const sm = window.soundManager;
    try {
        if (sm && sm.playURL) return sm.playURL(url, channel).catch(() => {});
    } catch (_) {}
    return new Promise((res) => { try { const a = new Audio(url); a.onended = a.onerror = () => res(); a.play().catch(() => res()); } catch (_) { res(); } });
}
const chime = (ok) => (ok ? play(`${CHIME}/correct.mp3`, "status") : Promise.resolve());

async function captureReading(passageText, presign, waitMs = 60000, onProgress, onStopReady) {
    const s3 = await presign();
    let speechConfig;
    try {
        speechConfig = await getSpeechConfig(); // token-based; key never reaches the browser
    } catch (e) {
        console.error("[Screener] speech token unavailable:", e && e.message);
        return { uploadId: null, meta: { text: "" } }; // graceful: scored as a miss; student can retry
    }
    let recognizer;
    let audioBlob = null;
    const meta = { text: "" };
    const target = String(passageText || "").trim().replace(/[^A-Za-z0-9_ -]+/gi, "").toLowerCase();

    const blob = await waitForVoice(
        Math.max(3, target.split(" ").length), 1, "norepeat", waitMs,
        {
            speechTimeOut: 7000,
            listening() {},
            empty() {},
            audioStreamBegan(stream, endTheStream) {
                const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(stream);
                recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
                const phraseList = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
                target.split(" ").forEach((w) => w && phraseList.addPhrase(w));
                const parts = [];
                const wordCount = (s) => String(s || "").trim().split(/\s+/).filter(Boolean).length;
                recognizer.recognizing = (s, e) => {
                    try {
                        const interim = e.result && typeof e.result.text === "string" ? e.result.text : "";
                        onProgress && onProgress(wordCount(parts.join(" ") + " " + interim));
                    } catch (_) {}
                };
                recognizer.recognized = (s, e) => {
                    try {
                        if (e.result && typeof e.result.text === "string" && e.result.text.length) {
                            parts.push(e.result.text);
                            meta.text = parts.join(" ").trim();
                            onProgress && onProgress(wordCount(meta.text));
                        }
                        const data = JSON.parse(e.result.json || "{}");
                        if (data.RecognitionStatus === "EndOfDictation") endTheStream();
                    } catch (_) {}
                };
                recognizer.canceled = () => endTheStream();
                // Let the caller stop the read manually (tap-to-finish); ends the stream so the
                // capture resolves and submit runs.
                onStopReady && onStopReady(() => { try { endTheStream(); } catch (_) {} });
                recognizer.startContinuousRecognitionAsync();
            },
            async closeStream() {
                if (recognizer) await new Promise((res, rej) => recognizer.stopContinuousRecognitionAsync(res, rej));
            },
        }
    );

    await wait(1);
    if (recognizer) {
        try { await new Promise((res, rej) => recognizer.stopContinuousRecognitionAsync(res, rej)); } catch (_) {}
        try { recognizer.close(); } catch (_) {}
    }
    if (blob && blob !== "done") audioBlob = blob;
    if (audioBlob) await uploadToS3(s3.url, audioBlob, "audio.wav", "audio/x-wav");
    return { uploadId: audioBlob ? s3.uploadId : null, meta };
}

function Mic({ status, onStart, onStop }) {
    let icon;
    if (status === "processing") icon = <Lottie options={{ loop: true, autoplay: true, animationData: processingAnimation }} width={64} height={64} />;
    else if (status === "pass") icon = <CorrectIcon />;
    else if (status === "fail") icon = <WrongIcon />;
    else if (status === "recording") icon = <StopRecordIcon />;
    else icon = <RecordIcon />;
    const onClick = status === "idle" ? onStart : status === "recording" ? onStop : undefined;
    return (
        <div
            role="button"
            tabIndex={0}
            className={clsx(styles.mic, status && styles[status])}
            style={{ cursor: onClick ? "pointer" : "default" }}
            onClick={onClick}
        >
            {icon}
        </div>
    );
}

export default function Screener() {
    const navigate = useNavigate();
    const [phase, setPhase] = useState("loading"); 
    const [attemptId, setAttemptId] = useState(null);
    const [item, setItem] = useState(null);
    const [intro, setIntro] = useState(null);
    const [selected, setSelected] = useState(null);
    const [feedback, setFeedback] = useState(null); 
    const [micStatus, setMicStatus] = useState("idle"); 
    const [busy, setBusy] = useState(false);
    const [seconds, setSeconds] = useState(null);
    const [retryMsg, setRetryMsg] = useState(null);
    const [readCount, setReadCount] = useState(0); // words highlighted blue while reading
    const [paused, setPaused] = useState(false);
    const pausedRef = useRef(false); // read synchronously inside the timer tick
    const shownPartRef = useRef(null);
    const tickRef = useRef(null);
    const timeoutRef = useRef(() => {});
    const stopRecRef = useRef(null);  // manual "stop reading" fn while recording
    const recBusyRef = useRef(false); // synchronous guard against double-tapping record
    const encEndRef = useRef(null);   // resolves when the encouragement video finishes
    const stopTimer = useCallback(() => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } }, []);

    const goToItem = useCallback(async (it) => {
        setSelected(null); setFeedback(null); setMicStatus("idle"); setRetryMsg(null); setReadCount(0);
        setPaused(false); pausedRef.current = false;
        if (it.part !== shownPartRef.current) {
            shownPartRef.current = it.part;
            setItem(it); setPhase("partIntro");
            await play(it.directions && it.directions.directionsAudio);
        }
        if (it.encouragement) {
            setItem(it); setPhase("encouragement");
            await new Promise((res) => {
                let done = false;
                const finish = () => { if (!done) { done = true; res(); } };
                encEndRef.current = finish;
                setTimeout(finish, 30000);
            });
            encEndRef.current = null;
        }
        setItem(it);
        setPhase(it.type === "reading" ? "reading" : "question");
        if (it.type === "choice") await play(it.audioUrl);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await screenerStart();
                if (res.alreadyCompleted) { navigate("/libraryIntro"); return; }
                setAttemptId(res.attemptId);
                setIntro(res.intro);
                setItem(res);
                setPhase("intro"); 
            } catch (e) { setPhase("error"); }
        })();
    }, [navigate]);

    const afterSubmit = useCallback(async (resp) => {
        if (resp.completed) {
            setPhase("done");
            await wait(1200);
            navigate("/libraryIntro");
            return;
        }
        await goToItem(resp.next);
    }, [goToItem, navigate]);

    const onChoose = useCallback(async (choice, isTimeout = false) => {
        if (busy) return;
        stopTimer();
        setBusy(true); setSelected(choice);
        try {
            const resp = await screenerSubmit({ attemptId, selected: choice });
            setFeedback(resp.isCorrect ? "correct" : "wrong");
            if (!isTimeout) chime(resp.isCorrect);
            await wait(isTimeout ? 400 : 750);
            await afterSubmit(resp);
        } finally { setBusy(false); }
    }, [busy, attemptId, afterSubmit, stopTimer]);

    const onRecord = useCallback(async () => {
        if (recBusyRef.current) return; 
        recBusyRef.current = true;
        const total = item.timer || 60;
        setBusy(true); setMicStatus("recording"); setSeconds(total); setRetryMsg(null); setReadCount(0);
        stopTimer();
        tickRef.current = setInterval(() => setSeconds((s) => (s <= 1 ? (stopTimer(), 0) : s - 1)), 1000);
        try {
            try { initiateVolumeMeter(); } catch (_) {}
            const onProgress = (n) => setReadCount((prev) => Math.max(prev, n));
            const registerStop = (fn) => { stopRecRef.current = fn; };
            const { uploadId, meta } = await captureReading(item.passage.text, () => screenerInitVoice(item.key), total * 1000, onProgress, registerStop);
            stopRecRef.current = null;
            stopTimer(); setSeconds(null);
            setMicStatus("processing");
            const resp = await screenerSubmit({ attemptId, uploadId, meta });
            if (resp.retry) {
                setMicStatus("fail");
                chime(false);
                await wait(1200);
                setRetryMsg("Almost! Let's read it one more time.");
                setMicStatus("idle");
                return;
            }
            setMicStatus(resp.isCorrect ? "pass" : "fail");
            chime(resp.isCorrect);
            await wait(900);
            await afterSubmit(resp);
        } finally { setBusy(false); stopTimer(); stopRecRef.current = null; recBusyRef.current = false; }
    }, [item, attemptId, afterSubmit, stopTimer]);

    const onStopReading = useCallback(() => {
        const fn = stopRecRef.current;
        if (!fn) return;
        stopRecRef.current = null;
        stopTimer(); setSeconds(null);
        setMicStatus("processing"); 
        try { fn(); } catch (_) {}
    }, [stopTimer]);

    const togglePause = useCallback(() => {
        setPaused((p) => { const np = !p; pausedRef.current = np; return np; });
    }, []);

    timeoutRef.current = () => { if (!busy) onChoose(null, true); };
    useEffect(() => {
        stopTimer();
        if (phase === "question" && item && item.timer) {
            setSeconds(item.timer);
            tickRef.current = setInterval(() => {
                if (pausedRef.current) return; 
                setSeconds((s) => {
                    if (s <= 1) { stopTimer(); timeoutRef.current(); return 0; }
                    return s - 1;
                });
            }, 1000);
        } else if (phase !== "reading") {
            setSeconds(null);
        }
        return stopTimer;
    }, [phase, item && item.key]);

    if (phase === "loading") return <div className={styles.screen}><div className={styles.spinner} /></div>;
    if (phase === "error") return <div className={styles.screen}><p className={styles.big}>Something went wrong. Please refresh.</p></div>;

    if (phase === "done") {
        return (
            <div className={styles.screen}>
                <div className={styles.doneBadge}><CheckIcon /></div>
                <h1 className={styles.big}>All done</h1>
                <p className={styles.directions}>Nice work today. Taking you to your library&hellip;</p>
            </div>
        );
    }

    if (phase === "intro") {
        return (
            <div className={styles.screen}>
                {intro && intro.video && <video className={styles.video} src={intro.video} autoPlay playsInline />}
                <button className={styles.cta} onClick={() => goToItem(item)}>Let's begin!</button>
            </div>
        );
    }

    if (phase === "partIntro") {
        const d = item && item.directions;
        return (
            <div className={styles.screen}>
                <div className={styles.partArrow}><ArrowIcon /></div>
                <h2 className={styles.partTitle}>{d && d.title}</h2>
                <p className={styles.directions}>{d && d.directions}</p>
            </div>
        );
    }

    if (phase === "encouragement") {
        const v = item && item.encouragement && item.encouragement.video;
        return (
            <div className={styles.screen}>
                {v && (
                    <video
                        className={styles.video}
                        src={v}
                        autoPlay
                        playsInline
                        onEnded={() => encEndRef.current && encEndRef.current()}
                        onError={() => encEndRef.current && encEndRef.current()}
                    />
                )}
            </div>
        );
    }

    const isReading = item && item.type === "reading";
    return (
        <div className={styles.screen}>
            {seconds != null && <Countdown seconds={seconds} />}
            {!isReading && (
                <button className={styles.pauseBtn} onClick={togglePause} aria-label="Pause"><PauseIcon /></button>
            )}

            {!isReading && (
                <>
                    <div className={styles.kicker}>{item.directions && item.directions.title}</div>
                    <button className={styles.replay} onClick={() => play(item.audioUrl)} aria-label="Hear it again"><span className={styles.qmark}>?</span></button>
                    <div className={styles.choices}>
                        {item.choices.map((c, i) => (
                            <button
                                key={`${c}-${i}`}
                                className={clsx(
                                    styles.choice,
                                    selected === c && feedback === "correct" && styles.correct,
                                    selected === c && feedback === "wrong" && styles.wrong,
                                )}
                                disabled={busy}
                                onClick={() => onChoose(c)}
                            >
                                {(item.part === "p1" || item.part === "p2") && KG_LETTERS.has(c)
                                    ? <span className={styles.kgGlyph}>{c}</span>
                                    : c}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {isReading && (
                <>
                    <div className={styles.passage}>
                        {(() => {
                            const tokens = String((item.passage && item.passage.text) || "").split(/(\s+)/);
                            let wi = 0;
                            return tokens.map((tok, i) => {
                                if (!tok || /^\s+$/.test(tok)) return <span key={i}>{tok}</span>;
                                const idx = wi++;
                                return <span key={i} className={clsx(styles.pword, idx < readCount && styles.pwordRead)}>{tok}</span>;
                            });
                        })()}
                    </div>
                    <div className={styles.micWrap}>
                        <Mic status={micStatus} onStart={onRecord} onStop={onStopReading} />
                        <div className={styles.micHint}>
                            {micStatus === "recording" ? "Listening… tap when you're done" : micStatus === "processing" ? "One moment…" : (retryMsg || "Tap the mic and read aloud")}
                        </div>
                    </div>
                </>
            )}

            {paused && (
                <div className={styles.pauseOverlay}>
                    <h1 className={styles.big}>Paused</h1>
                    <button className={styles.cta} onClick={togglePause}>Resume</button>
                </div>
            )}
        </div>
    );
}
